import { HttpClient, HttpParams, HttpResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { Device, DevicePatch, PageOfDevices, PageOfTokens, Token, TokenPatch, UserRequest } from './tokenbeheer.model';

@Injectable({ providedIn: 'root' })
export class TokenbeheerResource {
    private readonly httpClient = inject(HttpClient);

    private readonly baseUrl: string = '/services/apic-tokenbeheer/api';

    public getTokens(params?: {
        tokenStatusParam?: string;
        tokenTypeIdParam?: string[];
        limitParam?: number;
        cursorParam?: string;
        keyIdParam?: string;
    }): Observable<PageOfTokens> {
        let httpParams = {};

        if (params && params.tokenStatusParam) {
            httpParams = { ...httpParams, status: params.tokenStatusParam };
        }
        if (params && params.tokenTypeIdParam) {
            httpParams = { ...httpParams, type: params.tokenTypeIdParam.join(',') };
        }
        if (params && params.limitParam) {
            httpParams = { ...httpParams, limit: params.limitParam };
        }
        if (params && params.cursorParam) {
            httpParams = { ...httpParams, cursor: params.cursorParam };
        }
        if (params && params.keyIdParam) {
            httpParams = { ...httpParams, key_id: params.keyIdParam };
        }

        return this.httpClient.get<PageOfTokens>(`${this.baseUrl}/tokens`, { params: httpParams });
    }

    public getToken(id: string): Observable<Token> {
        return this.httpClient.get<Token>(`${this.baseUrl}/tokens/${id}`);
    }

    public patchToken(id: string, token: TokenPatch, authenticationTaskId?: string): Observable<HttpResponse<void>> {
        let headers = new HttpHeaders().append('Content-Type', 'application/merge-patch+json');
        if (authenticationTaskId) {
            headers = headers.append('X-Authentication-Task-Id', authenticationTaskId);
        }
        return this.httpClient.patch<void>(`${this.baseUrl}/tokens/${id}`, token, {
            observe: 'response',
            headers
        });
    }

    public getTokensForUsers(request: UserRequest): Observable<PageOfTokens> {
        return this.httpClient.get<PageOfTokens>(`${this.baseUrl}/tokens`, {
            params: this.mapQueryObjectToParams(this.filterUserRequest(request))
        });
    }

    public getDevices(params?: { limitParam?: number; cursorParam?: string }): Observable<PageOfDevices> {
        let httpParams = {};

        if (params && params.limitParam) {
            httpParams = { ...httpParams, limit: params.limitParam };
        }
        if (params && params.cursorParam) {
            httpParams = { ...httpParams, cursor: params.cursorParam };
        }

        return this.httpClient.get<PageOfDevices>(`${this.baseUrl}/devices`, { params: httpParams });
    }

    public getDevice(id: string): Observable<Device> {
        return this.httpClient.get<Device>(`${this.baseUrl}/devices/${id}`);
    }

    public getDevicesForUser(request: UserRequest): Observable<PageOfDevices> {
        return this.httpClient.get<PageOfDevices>(`${this.baseUrl}/devices`, {
            params: this.mapQueryObjectToParams(this.filterUserRequest(request))
        });
    }

    public patchDevice(id: string, device: DevicePatch, authenticationTaskId?: string): Observable<HttpResponse<void>> {
        let headers = new HttpHeaders().append('Content-Type', 'application/merge-patch+json');
        if (authenticationTaskId) {
            headers = headers.append('X-Authentication-Task-Id', authenticationTaskId);
        }
        return this.httpClient.patch<void>(`${this.baseUrl}/devices/${id}`, device, {
            observe: 'response',
            headers
        });
    }

    private mapQueryObjectToParams(request: { [key: string]: any }): HttpParams {
        return Object.keys(request)
            .filter((key) => !!request[key])
            .reduce<HttpParams>((params: HttpParams, key) => params.append(key, request[key].toString()), new HttpParams());
    }

    private filterUserRequest(request: UserRequest): UserRequest {
        if (!!request.user_eb_contract_id) {
            request.user_id = null;
            request.user_type = null;
        } else {
            request.user_eb_contract_id = null;
        }
        return request;
    }
}
